import '../../../scss/hotfood/components/hotfood-fable.scss'

import {
  Button,
  ButtonGroup,
  ButtonGroupSection,
  Card,
  Container,
  GridItem,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@sainsburys-tech/fable'
import { AlertCircleFilled } from '@sainsburys-tech/icons'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useApiClient } from '../../../common/AppContext/appContext'
import BackButton from '../../../common/components/BackButton/BackButton'
import ConfirmationBanner from '../../../common/components/Banners/ConfirmationBanner'
import ErrorRefresh from '../../../common/components/ErrorRefresh'
import Header from '../../../common/components/Header'
import Loading from '../../../common/components/Loading'
import { addBannerMessage } from '../../../common/Context/commonDispatch'
import { Context } from '../../../common/Context/context'
import { ErrorMessage } from '../../../common/enums/ErrorMessage'
import {
  GetHeaderMainMenus,
  headerMainMenus,
  headerPlannerTitles,
} from '../../../common/enums/HeaderItems'
import { Menu } from '../../../common/enums/MenuEnum'
import { PlannerName } from '../../../common/enums/PlannerNameEnum'
import useErrorHandler from '../../../common/hooks/useErrorHandler'
import { IResult } from '../../../common/types/IResult'
import { getUserStore } from '../../../utils/localStore'
import { getPizzaAndIsbProducts } from '../../api/displayApi'
import { createBatch } from '../../api/productionApi'
import { IBatch } from '../../types/IBatch'
import { IBatchItem } from '../../types/IBatchItem'
import { ISkuHeader } from '../../types/ISkuHeader'
import Incrementor from '../Production/Incrementor'

const PizzaPlacement = () => {
  const apiClient = useApiClient()
  const navigate = useNavigate()
  const { dispatch } = useContext(Context)
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)

  const [batch, setBatch] = useState<IBatch>({
    batchId: 0,
    items: [],
    programDescription: '',
    programId: 0,
    storeId: 0,
    temperaturesCount: 0,
  })
  const [isItemsValidated, setIsItemValidated] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [rangedIsbProducts, setRangedIsbProducts] = useState<ISkuHeader[]>([])
  const [rangedPizzaSlices, setRangedPizzaSlices] = useState<ISkuHeader[]>([])
  const [rangedWholePizzas, setRangedWholePizzas] = useState<ISkuHeader[]>([])
  const [headerNavigationItems, setHeaderNavigationItems] = useState(
    headerMainMenus.hotfoodcounterNavs,
  )

  const storeId = getUserStore().storeId

  const { handleError } = useErrorHandler(setErrorMessage)

  // sort batch items by skuName
  const sortBatchItems = useCallback(
    (batchItems: IBatchItem[]) =>
      batchItems.sort((a, b) =>
        a.skuName.toLowerCase() > b.skuName.toLowerCase()
          ? 1
          : b.skuName.toLowerCase() > a.skuName.toLowerCase()
            ? -1
            : 0,
      ),
    [],
  )

  // based on the skus, it will have a set of batchItems and add them to a batch
  // so that it will display the incrementor UI
  const buildBatchItems = useCallback(
    (rangedSkus: ISkuHeader[]) => {
      const batchItems = rangedSkus.reduce(
        (accItems: IBatchItem[], sku) => [
          ...accItems,
          {
            calculatedQuantity: 0,
            portionSize: 1,
            quantityProduced: 0,
            quantitySold: 0,
            skuId: sku.skuId,
            skuName: sku.skuName,
            toBeDisposedAt: new Date(),
          },
        ],
        [],
      )

      setBatch({ ...batch, items: sortBatchItems(batchItems) })
    },
    [batch, sortBatchItems],
  )

  useEffect(() => {
    const getPizzaAndIsbProductsTask = getPizzaAndIsbProducts(
      apiClient,
      storeId,
    )

    GetHeaderMainMenus(
      apiClient,
      getUserStore().storeId,
      PlannerName.HotFood,
    ).then((res) => setHeaderNavigationItems(res))

    Promise.all([getPizzaAndIsbProductsTask])
      .then((response: [ISkuHeader[]]) => {
        setRangedIsbProducts(response[0].filter((p) => p.isIsbProduct))
        setRangedPizzaSlices(response[0].filter((p) => p.isPizzaSlice))
        setRangedWholePizzas(
          response[0].filter((p) => !p.isIsbProduct && !p.isPizzaSlice),
        )
        buildBatchItems(response[0])
        setIsLoading(false)
      })
      .catch(() => setIsError(true))
  }, [apiClient, buildBatchItems, storeId])

  // change SKU quantity
  const onSkuQuantityChange = (skuId: number, value: number) => {
    // a set of non-changed items will be the base items
    const batchItems = batch.items.filter((i) => i.skuId !== skuId)

    // add updated item to the base items
    batchItems.push({
      calculatedQuantity: 0,
      portionSize: 1,
      quantityProduced: value,
      quantitySold: 0,
      skuId,
      skuName: batch.items.filter((i) => i.skuId === skuId)[0].skuName,
      toBeDisposedAt: new Date(),
    })

    // sorted batch items will be updated on the batch
    setBatch({ ...batch, items: sortBatchItems(batchItems) })
  }

  const scrollToTop = () => {
    window.scrollTo(0, 0)
  }

  const validateItems = async () => {
    const countedItems = batch.items.filter((item) => item.quantityProduced > 0)

    setIsItemValidated(countedItems.length > 0)
    if (countedItems.length > 0) {
      await handleSave({ ...batch, items: countedItems })
    } else {
      scrollToTop()
    }
  }

  const handleSave = async (batchToSave: IBatch) => {
    if (batchToSave.items.length === 0) {
      return
    }

    await createBatch(apiClient, {
      items: batchToSave.items.map((item) => ({
        quantityProduced: item.quantityProduced,
        skuId: item.skuId,
      })),
      storeId,
    })
      .then((batchRes: IResult) => {
        if (batchRes.isSuccess) {
          dispatch(
            addBannerMessage(
              <ConfirmationBanner
                message={'Pizza and ISB product(s) added successfully.'}
              />,
            ),
          )
          navigate('/hotfoodDisplay')
        } else {
          setIsError(true)
          handleError(ErrorMessage.SaveException)
          scrollToTop()
        }
      })
      .catch((e) => {
        setIsError(true)
        handleError(ErrorMessage.SaveException, e as Error)
        scrollToTop()
      })
  }

  const renderTableHeader = (title: string) => (
    <TableHeader>
      <TableRow>
        <TableCell>{title}</TableCell>
        <TableCell className={'c-hotfood-placement-incrementor-heading'}>
          Turbo-serve
        </TableCell>
      </TableRow>
    </TableHeader>
  )

  const renderTableBody = (skus: ISkuHeader[]) => (
    <TableBody>
      {skus.map((sku: ISkuHeader) => (
        <TableRow key={sku.skuId}>
          <TableCell className='c-batch-table-responsive-title'>
            {sku.skuName}
          </TableCell>

          {batch.items
            .filter((bi) => bi.skuId === sku.skuId)
            .map((itemFromCabinet) => (
              <>
                <TableCell className='c-hotfood-placement-incrementor'>
                  <Incrementor
                    id={itemFromCabinet.skuId}
                    step={1}
                    value={itemFromCabinet.quantityProduced}
                    setValue={(skuId: number, value: number) =>
                      onSkuQuantityChange(skuId, value)
                    }
                  />
                </TableCell>
              </>
            ))}
        </TableRow>
      ))}
    </TableBody>
  )

  const renderCardHeader = (title: string) => <h6>{title}</h6>

  const renderCardRows = (skus: ISkuHeader[]) => (
    <>
      {skus.map((sku: ISkuHeader) => (
        <div className='display-pizza-slice-card-row'>
          <div key={sku.skuId}>{sku.skuName}</div>

          {batch.items
            .filter((bi) => bi.skuId === sku.skuId)
            .map((itemFromCabinet) => (
              <>
                <div>
                  <GridItem colSpan={8}>
                    <Incrementor
                      id={itemFromCabinet.skuId}
                      step={1}
                      value={itemFromCabinet.quantityProduced}
                      setValue={(skuId: number, value: number) =>
                        onSkuQuantityChange(skuId, value)
                      }
                    />
                  </GridItem>
                </div>
              </>
            ))}
        </div>
      ))}
    </>
  )

  return (
    <div>
      <Header
        title={headerPlannerTitles.hotfood}
        navItems={headerNavigationItems}
        activeMenuType={Menu.Display}
      />
      <Container>
        {isLoading && <Loading />}
        {isError && (
          <ErrorRefresh
            message={errorMessage ?? ErrorMessage.GeneralException}
          />
        )}

        {!isLoading && (
          <>
            {BackButton('/hotfoodDisplay', 'Back to On Display')}
            <h4>Pizza and ISB products</h4>

            <div style={{ fontSize: 'var(--fable-text-body-1-font-size)' }}>
              Add pizza or ISB products to your display to keep track of removal
              times.
              {!isItemsValidated && (
                <div>
                  <AlertCircleFilled className='c-form-alert-icon-red' />
                  Please select at least one portion before adding to display
                </div>
              )}
            </div>

            <Card orientation='vertical'>
              {/* ISB product(s) table */}
              {rangedIsbProducts.length > 0 && (
                <Table
                  className='c-display-hotfood-pizza-slice-table-container'
                  testid='isb-table'
                >
                  {renderTableHeader('ISB')}
                  {renderTableBody(rangedIsbProducts)}
                </Table>
              )}

              {/* pizza slice(s) table */}
              {rangedPizzaSlices.length > 0 && (
                <Table
                  className='c-display-hotfood-pizza-slice-table-container'
                  testid='pizza-slice-table'
                >
                  {renderTableHeader('Pizza slices')}
                  {renderTableBody(rangedPizzaSlices)}
                </Table>
              )}

              {/* whole pizza(s) table */}
              {rangedWholePizzas.length > 0 && (
                <Table
                  className='c-display-hotfood-pizza-slice-table-container'
                  testid='whole-pizza-table'
                >
                  {renderTableHeader('Whole pizzas')}
                  {renderTableBody(rangedWholePizzas)}
                </Table>
              )}

              <div className='c-display-hotfood-pizza-slice-card-container'>
                {/* ISB product(s) cards */}
                {rangedIsbProducts.length > 0 && (
                  <>
                    {renderCardHeader('ISB')}
                    {renderCardRows(rangedIsbProducts)}
                  </>
                )}

                {/* pizza slice(s) cards */}
                {rangedPizzaSlices.length > 0 && (
                  <div>
                    {renderCardHeader('Pizza slices')}
                    {renderCardRows(rangedPizzaSlices)}
                  </div>
                )}

                {/* whole pizza(s) cards */}
                {rangedWholePizzas.length > 0 && (
                  <div>
                    {renderCardHeader('Whole pizzas')}
                    {renderCardRows(rangedWholePizzas)}
                  </div>
                )}
              </div>

              <ButtonGroup>
                <ButtonGroupSection className='hotfood-full-width'>
                  <Button
                    onClick={validateItems}
                    testid='add-to-display-button'
                  >
                    Add to display
                  </Button>

                  <Button
                    onClick={() => navigate('/hotfoodDisplay')}
                    testid='cancel-button'
                  >
                    Cancel
                  </Button>
                </ButtonGroupSection>
              </ButtonGroup>
            </Card>
          </>
        )}
      </Container>
    </div>
  )
}

export default PizzaPlacement
